import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';

const MONTH_PERIODS = [
  { value: 3, label: '3 Months' },
  { value: 6, label: '6 Months' },
  { value: 12, label: '12 Months' },
];

const LIMIT_OPTIONS = [
  { value: 5, label: 'Top 5' },
  { value: 10, label: 'Top 10' },
  { value: 25, label: 'Top 25' },
  { value: 50, label: 'All' },
];

const monthLabel = (monthKey) => {
  const [y, m] = monthKey.split('-');
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

export default function TopSpendersView({ onViewCharges, refreshKey }) {
  const [months, setMonths] = useState(3);
  const [limit, setLimit] = useState(5);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getTopByMonth(months, limit);
      setData(d.months);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [months, limit]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div style={styles.wrap}>
      <div style={styles.intro}>
        <p style={styles.introText}>
          Your biggest individual purchases, month by month, side by side — the fastest way to spot what needs cutting.
        </p>
      </div>

      <div style={styles.controlsRow}>
        <div style={styles.pillGroup}>
          {MONTH_PERIODS.map((p) => (
            <button
              key={p.value}
              style={{ ...styles.pill, ...(months === p.value ? styles.pillActive : {}) }}
              onClick={() => setMonths(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div style={styles.pillGroup}>
          {LIMIT_OPTIONS.map((o) => (
            <button
              key={o.value}
              style={{ ...styles.pill, ...(limit === o.value ? styles.pillActive : {}) }}
              onClick={() => setLimit(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <p style={styles.empty}>Loading...</p>
      ) : !data || data.every((m) => m.transactions.length === 0) ? (
        <p style={styles.empty}>No transactions found for this range.</p>
      ) : (
        <div style={styles.columnsScroll}>
          <div style={styles.columnsRow}>
            {data.map((m) => (
              <MonthColumn key={m.month} month={m} onViewCharges={onViewCharges} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MonthColumn({ month, onViewCharges }) {
  const total = month.transactions.reduce((sum, t) => sum + t.amount, 0);
  return (
    <div style={styles.column}>
      <div style={styles.columnHeader}>
        <span style={styles.columnMonth}>{monthLabel(month.month)}</span>
        <span style={styles.columnTotal}>{fmt.currency(total)}</span>
      </div>
      {month.transactions.length === 0 ? (
        <p style={styles.columnEmpty}>No transactions</p>
      ) : (
        <div style={styles.columnList}>
          {month.transactions.map((t, i) => (
            <button
              key={t.id}
              style={styles.row}
              onClick={() => onViewCharges?.(t.merchantName || t.description)}
              title="View all transactions from this merchant"
            >
              <span style={styles.rowRank}>{i + 1}</span>
              <span style={styles.rowInfo}>
                <span style={styles.rowName}>{t.merchantName || t.description}</span>
                <span style={styles.rowDate}>{fmt.shortDate(t.date)}{t.category?.[0] ? ` · ${t.category[0]}` : ''}</span>
              </span>
              <span style={styles.rowAmount}>{fmt.currency(t.amount)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: { padding: '0' },
  intro: { marginBottom: '1rem' },
  introText: { color: '#6b7280', fontSize: '0.78rem', margin: 0 },
  controlsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '0.6rem',
    marginBottom: '1.25rem',
  },
  pillGroup: { display: 'flex', gap: '0.4rem' },
  pill: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '999px',
    padding: '5px 12px',
    fontSize: '0.72rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  pillActive: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: '1px solid #00d4aa',
  },
  empty: { color: '#4b5563', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' },
  columnsScroll: {
    overflowX: 'auto',
    paddingBottom: '0.5rem',
  },
  columnsRow: {
    display: 'flex',
    gap: '0.75rem',
    minWidth: 'min-content',
  },
  column: {
    flex: '0 0 220px',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '12px',
    padding: '0.75rem',
  },
  columnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '0.6rem',
    paddingBottom: '0.5rem',
    borderBottom: '1px solid #1e2a3a',
  },
  columnMonth: {
    color: '#fff',
    fontSize: '0.85rem',
    fontWeight: '800',
    fontFamily: "'Syne', sans-serif",
  },
  columnTotal: {
    color: '#ef4444',
    fontSize: '0.72rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  columnEmpty: { color: '#374151', fontSize: '0.72rem', textAlign: 'center', padding: '1rem 0' },
  columnList: { display: 'flex', flexDirection: 'column', gap: '2px' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    background: 'transparent',
    border: 'none',
    borderRadius: '6px',
    padding: '5px 4px',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
  },
  rowRank: {
    color: '#374151',
    fontSize: '0.65rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
    width: '14px',
    flexShrink: 0,
  },
  rowInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 },
  rowName: {
    color: '#e5e7eb',
    fontSize: '0.72rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rowDate: {
    color: '#4b5563',
    fontSize: '0.62rem',
    fontFamily: "'DM Mono', monospace",
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rowAmount: {
    color: '#fff',
    fontSize: '0.72rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
    flexShrink: 0,
  },
};
